import { IVSelectItem } from "@/lib/interfaces/v-select-item.interface";
import { PageDataHandler } from "@/lib/utility/data/page-data-handler";
import { AbstractPageDataProvider } from "@/lib/utility/data/page-data-provider.abstract";
import { PageFilterTypes } from "@/lib/utility/data/page-filter-types.enum";
import { IPageViewModel } from "@/lib/utility/data/page-view-model.interface";
import { IPageFilterElement, PageFilterElement } from "@/models/page-filter-element.entity";
import { IReport, Report } from "@/models/report.entity";
import reportFleetService from "@/services/mrfiktiv/services/fleet/reportFleetService";
import {
  MrfiktivFleetReportControllerGetAllParamsGen,
  MrfiktivReportControllerFindAllParamsGen,
  MrfiktivReportViewModelGen
} from "@/services/mrfiktiv/v1/data-contracts";
import { CoverageTypeEnum } from "@/store/enum/coverage-type.enum";
import { DamageTypeEnum } from "@/store/enum/damage-type.enum";
import { ProgressStatusEnum } from "@/store/enum/partner/progress.status.enum";
import { PaginatedBaseStore } from "@/store/paginated-base.store";
import store from "@/store/VuexPlugin";
import { Action, Module, Mutation, getModule } from "vuex-module-decorators";
import { FleetDamageDataAccessLayer } from "../access-layers/fleet-damage.access-layer";
import { PaginationFilterListElement } from "../base-pagination.store";
import { FleetManagerModule } from "./manager-fleet.store";
import { RepairStatusEnum } from "@/lib/enum/repair.enum";
import { TripTypeEnum } from "@/lib/enum/trip-type.enum";
@Module({
  dynamic: true,
  namespaced: true,
  name: "damageFleetStore",
  store
})
export class DamagesFleetStore extends PaginatedBaseStore<
  IReport,
  MrfiktivFleetReportControllerGetAllParamsGen & { fleetIdFilter: string[] }
> {
  _data = FleetDamageDataAccessLayer;
  _pageProvider = new (class extends AbstractPageDataProvider<IReport, MrfiktivReportControllerFindAllParamsGen> {
    async getPage(query: MrfiktivFleetReportControllerGetAllParamsGen): Promise<IPageViewModel<IReport>> {
      const res = await reportFleetService.findAll(query, { ids: FleetManagerModule.selectedFleetIds ?? [] });

      const meta = res.meta;
      const data = (res.data?.map((d) => new Report(d)) ?? []) as IReport[];

      return { meta, data };
    }
  })();

  _pager = new PageDataHandler(this._data, this._pageProvider);

  _currentReport: MrfiktivReportViewModelGen | null = null;

  hiddenFilter: IPageFilterElement[] = [
    new PageFilterElement({ key: "progressStatus", value: ProgressStatusEnum.DELETED, operation: "$ne" })
  ];

  filterOptions: PaginationFilterListElement[] = [
    { key: "timestamps.created", type: PageFilterTypes.DATE, displayName: "objects.timestamp.created" },
    { key: "numberplate", type: PageFilterTypes.STRING, displayName: "objects.report.numberplate" },
    { key: "costCenter", type: PageFilterTypes.STRING, displayName: "objects.report.costCenter" },
    { key: "claimNumber", type: PageFilterTypes.STRING, displayName: "objects.reportControl.id" },
    { key: "status", type: PageFilterTypes.STRING, displayName: "objects.report.status" },
    {
      key: "progressStatus",
      type: PageFilterTypes.ENUM,
      config: {
        items: Object.values(ProgressStatusEnum)
          .filter((v) => v !== ProgressStatusEnum.DELETED)
          .map((reportStatus) => {
            return {
              text: `partner.common.reportProgressStatus.${reportStatus}`,
              value: reportStatus
            } as IVSelectItem;
          }),
        itemValue: "value"
      },
      displayName: "objects.report.progressStatus"
    },
    { key: "isFixed", type: PageFilterTypes.BOOLEAN, displayName: "objects.report.isFixed" },
    {
      key: "coverageType",
      type: PageFilterTypes.ENUM,
      config: {
        items: Object.values(CoverageTypeEnum).map((type) => {
          return {
            text: `enums.CoverageTypeEnum.${type}`,
            value: type
          } as IVSelectItem;
        }),
        itemValue: "value"
      },
      displayName: "objects.report.coverageType"
    },
    {
      key: "damageType",
      type: PageFilterTypes.ENUM,
      config: {
        items: Object.values(DamageTypeEnum).map((type) => {
          return {
            text: `enums.DamageTypeEnum.${type}`,
            value: type
          } as IVSelectItem;
        }),
        itemValue: "value"
      },
      displayName: "objects.report.damageType"
    },
    {
      key: "repair.status",
      type: PageFilterTypes.ENUM,
      config: {
        items: Object.values(RepairStatusEnum).map((type) => {
          return {
            text: `enums.RepairStatusEnum.${type}`,
            value: type
          } as IVSelectItem;
        }),
        itemValue: "value"
      },
      displayName: "objects.report.repair.status"
    },
    { key: "repair.startDate", type: PageFilterTypes.DATE, displayName: "objects.report.repair.startDate" },
    { key: "repair.endDate", type: PageFilterTypes.DATE, displayName: "objects.report.repair.endDate" },
    { key: "customerName", type: PageFilterTypes.STRING, displayName: "report.user.MyDamageDetail.customerName" },
    {
      key: "tripType",
      type: PageFilterTypes.ENUM,
      config: {
        items: Object.values(TripTypeEnum).map((type) => {
          return {
            text: `enums.TripTypeEnum.${type}`,
            value: type
          } as IVSelectItem;
        }),
        itemValue: "value"
      },
      displayName: "objects.report.tripType"
    },
    { key: "vehicleId", type: PageFilterTypes.STRING, displayName: "objects.vehicle.vehicleId" }
  ].map((f) => new PaginationFilterListElement(f));

  get currentReport() {
    return this._currentReport;
  }

  @Mutation
  _mutateCurrentReport(data: MrfiktivReportViewModelGen) {
    this._currentReport = data;
  }

  @Action
  setCurrentReport(data: MrfiktivReportViewModelGen) {
    this.context.commit("_mutateCurrentReport", data);
  }

  @Mutation
  private _mutateOne(request: MrfiktivReportViewModelGen) {
    this._data.set(new Report(request));
  }

  @Action
  async getOne(data: { partnerId: string; fleetId: string; id: string }): Promise<MrfiktivReportViewModelGen> {
    const report = await reportFleetService.findOne(data.partnerId, data.fleetId, data.id);

    this.context.commit("_mutateOne", new Report(report));
    this.context.commit("_mutateCurrentReport", new Report(report));

    return report;
  }
}

export const DamageFleetModule = getModule(DamagesFleetStore);
